import React from 'react';
import TutorialLayout from '../../components/TutorialLayout';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { getHostname } from '../../utils/getHostname';
import '../../components/DownloadProfileModal.css';
import './Mac.css';

function Ubuntu() {
  const hostname = getHostname();
  
  useDocumentTitle('Linux VPN Setup');
  
  return (
    <TutorialLayout
      title="Linux VPN Setup with Pritunl Client"
      description={`This guide will walk you through downloading and installing the Pritunl client on your Linux system, and loading your VPN profile zip file from ${hostname}.`}
    >
      <h4>1. Download Your VPN Profile</h4>
      <p>
        After a successful purchase, or when you open the link from your email, you will see a popup like the one below.
        Use it to download your VPN profile.
      </p>
      <ol>
        <li>Click the "Download VPN Profile" button in the popup to download your VPN profile zip file.</li>
        <li>Save the zip file to a location you can easily find, such as your Downloads folder.</li>
      </ol>
      <div className="post-images">
        <img src="/assets/images/tutorials/login.png" alt="Download VPN profile popup after purchase or email link" />
      </div>
      <p><strong>Note:</strong> Keep this zip file safe as you'll need it to configure the Pritunl client.</p>

      <h4>2. Download and Install Pritunl Client</h4>
      <p>Now you need to download and install the Pritunl client for Linux:</p>
      <ol>
        <li>
          Visit the Pritunl Client download page to find installation instructions for your Linux distribution:
          <div style={{ marginTop: '15px', marginBottom: '15px', maxWidth: '450px' }}>
            <a 
              href="https://client.pritunl.com/" 
              className="download-vpn-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              Visit Pritunl Client Download Page
            </a>
          </div>
        </li>
        <li>Once the download is complete, locate the downloaded file (usually in your Downloads folder).</li>
        <li>
          <strong>For Debian/Ubuntu (.deb file):</strong> Open a terminal and run:
          <pre style={{ backgroundColor: '#f7fafc', padding: '12px', borderRadius: '4px', marginTop: '8px', overflowX: 'auto' }}>
            sudo dpkg -i pritunl-client-electron_*.deb
          </pre>
          If you encounter dependency issues, run:
          <pre style={{ backgroundColor: '#f7fafc', padding: '12px', borderRadius: '4px', marginTop: '8px', overflowX: 'auto' }}>
            sudo apt-get install -f
          </pre>
        </li>
        <li>
          <strong>For Red Hat/Fedora (.rpm file):</strong> Open a terminal and run:
          <pre style={{ backgroundColor: '#f7fafc', padding: '12px', borderRadius: '4px', marginTop: '8px', overflowX: 'auto' }}>
            sudo rpm -i pritunl-client-electron_*.rpm
          </pre>
          Or for newer systems:
          <pre style={{ backgroundColor: '#f7fafc', padding: '12px', borderRadius: '4px', marginTop: '8px', overflowX: 'auto' }}>
            sudo dnf install pritunl-client-electron_*.rpm
          </pre>
        </li>
        <li>After installation, you can find Pritunl in your applications menu.</li>
      </ol>

      <h4>3. Load Your VPN Profile</h4>
      <p>Now that you have Pritunl installed and your VPN profile downloaded, it's time to import the profile:</p>
      <ol>
        <li>Open the Pritunl application from your applications menu.</li>
        <li>When Pritunl opens, you'll see the main interface. Look for an "Import" or "Import Profile" button (usually located at the top of the window or in a menu).</li>
        <li>Click the "Import" button and navigate to where you saved your VPN profile zip file.</li>
        <li>Select the zip file and click "Open" or "Import".</li>
        <li>The VPN profile will be imported and should appear in your list of available VPN connections.</li>
      </ol>
      <div className="post-images">
        <img src="/assets/images/tutorials/import.png" alt="Importing VPN profile in Pritunl client" />
      </div>
      <p><strong>Alternative method:</strong> You can also drag and drop the zip file directly onto the Pritunl application window to import it.</p>

      <h4>4. Connect to VPN</h4>
      <p>Once your profile is loaded, you can connect to the VPN:</p>
      <ol>
        <li>In the Pritunl client, you should see your imported VPN profile listed.</li>
        <li>Click on the profile name or the "Connect" button next to it.</li>
        <li>Wait for the connection to establish. You'll see a status indicator showing "Connected" when successful.</li>
        <li>The Pritunl client icon in your system tray will also show the connection status.</li>
      </ol>
      <div className="post-images">
        <img src="/assets/images/tutorials/connect.png" alt="Connecting to VPN in Pritunl client" />
      </div>

      <h4>5. Verify Your Connection</h4>
      <p>To verify that your VPN connection is working:</p>
      <ol>
        <li>Check the status in the Pritunl client - it should show "Connected".</li>
        <li>You can visit a website like <a href="https://whatismyipaddress.com" target="_blank" rel="noopener noreferrer">whatismyipaddress.com</a> to verify your IP address has changed.</li>
        <li>The system tray icon will also indicate the connection status.</li>
      </ol>

      <h4>6. Disconnect from VPN</h4>
      <p>To disconnect from the VPN:</p>
      <ol>
        <li>Click on the VPN profile in Pritunl and select "Disconnect", or</li>
        <li>Right-click the Pritunl icon in your system tray and select "Disconnect".</li>
      </ol>

      <h4>7. Enjoy {hostname}</h4>
      <p>You're all set! Once connected, all your internet traffic will be routed through the {hostname} VPN server, providing you with secure and private browsing. You can connect and disconnect anytime using the Pritunl client.</p>
      
      <p><strong>Tips:</strong></p>
      <ul>
        <li>You can set Pritunl to start automatically when you log in by adding it to your desktop environment's autostart applications.</li>
        <li>If you encounter any connection issues, make sure your VPN profile zip file is not corrupted and that you're using the correct login credentials.</li>
        <li>Keep your VPN profile zip file in a secure location in case you need to reimport it later.</li>
        <li>If you need to run Pritunl from the command line, you can typically find it at <code>/usr/bin/pritunl-client-electron</code> or launch it from your applications menu.</li>
      </ul>
    </TutorialLayout>
  );
}

export default Ubuntu;
